import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { TimePicker12h } from '../components/TimePicker12h';
import { COUNTRIES } from '../lib/countries';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, Send, Plus, Trash2, User, Pencil } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
const CFV_LOCATIONS = ['Mumbai', 'Vankas (U1)', 'Malkhet (U2)'];
const CFV_BRANDS = ['Univicoustic', 'Uflex', 'Unitile'];
const CFV_ENTITIES = ['UOSPL', 'UAFPL', 'UAPL'];
const CFV_DURATIONS = ['Full Day', 'Half Day', 'Overnight'];

export default function EditTicketPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader, employee } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Client Factory Visit — visit-location itinerary editor
  const [cfvSeg, setCfvSeg] = useState(null);
  const [cfvEditIndex, setCfvEditIndex] = useState(null);
  const emptySeg = () => ({ location: '', brand: '', entity: '', arrival_date: '', expected_time: '', departure_date: '', departure_time: '', duration: 'Full Day' });
  const startAddSeg = () => { setCfvEditIndex(null); setCfvSeg(emptySeg()); };
  const startEditSeg = (i) => { setCfvEditIndex(i); setCfvSeg({ ...formData.cfv_visit_locations[i] }); };
  const cancelSeg = () => { setCfvSeg(null); setCfvEditIndex(null); };
  const saveSeg = () => {
    if (!cfvSeg.location) { toast.error('Select a visit location'); return; }
    if (!cfvSeg.arrival_date) { toast.error('Select an arrival date'); return; }
    if (!cfvSeg.departure_date) { toast.error('Select a departure date'); return; }
    if (cfvSeg.departure_date < cfvSeg.arrival_date) { toast.error('Departure cannot be before arrival'); return; }
    setFormData(prev => {
      const list = [...(prev.cfv_visit_locations || [])];
      if (cfvEditIndex !== null) list[cfvEditIndex] = cfvSeg; else list.push(cfvSeg);
      return { ...prev, cfv_visit_locations: list };
    });
    cancelSeg();
  };
  const deleteSeg = (i) => setFormData(prev => ({ ...prev, cfv_visit_locations: prev.cfv_visit_locations.filter((_, idx) => idx !== i) }));
  const [formData, setFormData] = useState({});

  const fetchTicket = useCallback(async () => {
    try {
      const res = await fetch(`${API}/tickets/${ticketId}`, {
        headers: getAuthHeader(),
        
      });
      
      if (res.ok) {
        const data = await res.json();
        setTicket(data);
        // Initialize form data with all ticket fields
        setFormData({
          description: data.description || '',
          priority: data.priority || 'Medium',
          // Courier fields
          courier_type: data.courier_type || '',
          courier_from: data.courier_from || '',
          courier_to: data.courier_to || '',
          courier_contents: data.courier_contents || '',
          courier_pickup_point: data.courier_pickup_point || '',
          // Guest fields
          guest_name: data.guest_name || '',
          guest_company: data.guest_company || '',
          guest_purpose: data.guest_purpose || '',
          guest_date: data.guest_date || '',
          guest_time: data.guest_time || '',
          guest_arrival_date: data.guest_arrival_date || '',
          guest_departure_date: data.guest_departure_date || '',
          guest_food_required: data.guest_food_required || 'No',
          guest_car_parking: data.guest_car_parking || 'No',
          // Event fields
          event_name: data.event_name || '',
          event_date: data.event_date || '',
          event_time: data.event_time || '',
          event_location: data.event_location || '',
          event_attendees: data.event_attendees?.toString() || '',
          event_requirements: data.event_requirements || '',
          // Room fields
          room_type: data.room_type || '',
          room_location: data.room_location || '',
          room_date: data.room_date || '',
          room_start_time: data.room_start_time || '',
          room_end_time: data.room_end_time || '',
          room_attendees: data.room_attendees?.toString() || '',
          room_requirements: data.room_requirements || '',
          // Promotion Box fields
          promo_employee_name: data.promo_employee_name || '',
          promo_designation: data.promo_designation || '',
          promo_department: data.promo_department || '',
          promo_effective_date: data.promo_effective_date || '',
          promo_special_instructions: data.promo_special_instructions || '',
          // Desk Decor - Birthday fields
          birthday_employee_name: data.birthday_employee_name || '',
          birthday_date: data.birthday_date || '',
          birthday_department: data.birthday_department || '',
          birthday_location: data.birthday_location || '',
          // HR Induction fields
          induction_employee_name: data.induction_employee_name || '',
          induction_employee_id: data.induction_employee_id || '',
          induction_designation: data.induction_designation || '',
          induction_joining_date: data.induction_joining_date || '',
          induction_location: data.induction_location || '',
          induction_department: data.induction_department || '',
          induction_employee_type: data.induction_employee_type || '',
          induction_tshirt_size: data.induction_tshirt_size || '',
          induction_photo: data.induction_photo || null,
          induction_need_id_card: data.induction_need_id_card || 'no',
          induction_dob: data.induction_dob || '',
          induction_date_of_issue: data.induction_date_of_issue || '',
          induction_masked_aadhaar: data.induction_masked_aadhaar || '',
          induction_blood_group: data.induction_blood_group || '',
          induction_emergency_number: data.induction_emergency_number || '',
          induction_office_address: data.induction_office_address || '',
          induction_need_mediclaim: data.induction_need_mediclaim || 'no',
          induction_mediclaim_members: data.induction_mediclaim_members || [],
          induction_facility_visiting_card: data.induction_facility_visiting_card || false,
          induction_facility_hotel: data.induction_facility_hotel || false,
          induction_facility_transport: data.induction_facility_transport || false,
          induction_food_required: data.induction_food_required || 'no',
          induction_entity: data.induction_entity || '',
          // Insurance fields
          insurance_employee_name: data.insurance_employee_name || '',
          insurance_employee_id: data.insurance_employee_id || '',
          insurance_exit_date: data.insurance_exit_date || '',
          insurance_employees: data.insurance_employees || [],
          // Stationery fields
          stationery_type: data.stationery_type || '',
          stationery_items: data.stationery_items || '',
          stationery_description: data.stationery_description || '',
          stationery_required_by_date: data.stationery_required_by_date || '',
          // Location for all requests
          ticket_location: data.ticket_location || '',
          // Repairs & Maintenance
          maintenance_category: data.maintenance_category || '',
          maintenance_other_text: data.maintenance_other_text || '',
          // Client Factory Visit
          cfv_brand: data.cfv_brand || '',
          cfv_guest_name: data.cfv_guest_name || '',
          cfv_company: data.cfv_company || '',
          cfv_guests_count: data.cfv_guests_count || '',
          cfv_purpose: data.cfv_purpose || '',
          cfv_need_invite: data.cfv_need_invite || 'No',
          cfv_photography: data.cfv_photography || 'No',
          cfv_videography: data.cfv_videography || 'No',
          cfv_transport: data.cfv_transport || 'No',
          cfv_visitors: data.cfv_visitors || [],
          cfv_brands: data.cfv_brands || [],
          cfv_food_required: data.cfv_food_required || 'No',
          cfv_meals: data.cfv_meals || [],
          cfv_dietary: data.cfv_dietary || '',
          cfv_stay_required: data.cfv_stay_required || 'No',
          cfv_stay_days: data.cfv_stay_days || '',
          cfv_giveaway: data.cfv_giveaway || [],
          cfv_giveaway_other: data.cfv_giveaway_other || '',
          cfv_car_parking: data.cfv_car_parking || 'No',
          cfv_primary_objective: data.cfv_primary_objective || '',
          cfv_primary_objective_other: data.cfv_primary_objective_other || '',
          cfv_project_linked: data.cfv_project_linked || 'No',
          cfv_project_name: data.cfv_project_name || '',
          cfv_project_location: data.cfv_project_location || '',
          cfv_project_stage: data.cfv_project_stage || '',
          cfv_industry_segment: data.cfv_industry_segment || '',
          cfv_industry_segment_other: data.cfv_industry_segment_other || '',
          cfv_area_of_interest: data.cfv_area_of_interest || '',
          cfv_areas_covered: data.cfv_areas_covered || '',
          cfv_products_demonstrated: data.cfv_products_demonstrated || '',
          cfv_lab_testing: data.cfv_lab_testing || 'No',
          cfv_lab_testing_details: data.cfv_lab_testing_details || '',
          cfv_team_members: data.cfv_team_members || '',
          cfv_senior_mgmt: data.cfv_senior_mgmt || 'No',
          cfv_presentation: data.cfv_presentation || 'No',
          cfv_presentation_type: data.cfv_presentation_type || '',
          cfv_conference_room: data.cfv_conference_room || 'No',
          cfv_visit_locations: data.cfv_visit_locations || [],
        });
      } else {
        toast.error('Ticket not found');
        navigate('/tickets');
      }
    } catch (error) {
      toast.error('Failed to load ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  }, [ticketId, getAuthHeader, navigate]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  // Check if user can edit
  const canEdit = ticket && 
    ['Draft', 'Open'].includes(ticket.status) && 
    ticket.requester_email === employee?.email;

  // Mediclaim member helpers
  const addMediclaimMember = () => {
    setFormData(prev => ({
      ...prev,
      induction_mediclaim_members: [
        ...prev.induction_mediclaim_members,
        { full_name: '', relation: '', dob: '', doj: '', grade: '', sum_insured: '' }
      ]
    }));
  };

  const removeMediclaimMember = (index) => {
    setFormData(prev => ({
      ...prev,
      induction_mediclaim_members: prev.induction_mediclaim_members.filter((_, i) => i !== index)
    }));
  };

  const updateMediclaimMember = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      induction_mediclaim_members: prev.induction_mediclaim_members.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  // Validate HR Induction mandatory fields
  const validateHRInduction = () => {
    const errors = [];
    if (!formData.induction_employee_name?.trim()) errors.push('Employee Name');
    if (!formData.induction_employee_id?.trim()) errors.push('Employee ID');
    if (!formData.induction_designation?.trim()) errors.push('Designation');
    if (!formData.induction_joining_date) errors.push('Joining Date');
    if (!formData.induction_location?.trim()) errors.push('Location');
    if (!formData.induction_department?.trim()) errors.push('Department');
    if (!formData.induction_employee_type) errors.push('Employee Type');
    if (!formData.induction_entity) errors.push('Entity');
    return errors;
  };

  // Validate Insurance Removal mandatory fields
  const validateInsuranceRemoval = () => {
    const errors = [];
    if (!formData.insurance_employee_name?.trim()) errors.push('Employee Name');
    if (!formData.insurance_employee_id?.trim()) errors.push('Employee ID');
    if (!formData.insurance_exit_date) errors.push('Date of Exit');
    return errors;
  };

  const handleSave = async (submitAfterSave = false) => {
    // For submitting drafts, validate all mandatory fields based on request type
    if (submitAfterSave) {
      // Description is optional for most request types
      // Only mandatory for types that have no other specific fields
      const descriptionOnlyTypes = ['Other', 'Office Assistant Help'];
      if (descriptionOnlyTypes.includes(ticket?.request_type) && !formData.description.trim()) {
        toast.error('Please provide a description');
        return;
      }

      // HR Induction / Onboarding specific validation
      if (ticket?.request_type === 'Onboarding' || ticket?.request_type === 'HR – Induction Kit Request') {
        const missingFields = validateHRInduction();
        if (missingFields.length > 0) {
          toast.error(`Please fill all mandatory fields: ${missingFields.join(', ')}`);
          return;
        }
      }

      // Insurance Removal specific validation
      if (ticket?.request_type === 'Insurance Removal Request') {
        const missingFields = validateInsuranceRemoval();
        if (missingFields.length > 0) {
          toast.error(`Please fill all mandatory fields: ${missingFields.join(', ')}`);
          return;
        }
      }

      // Client Factory Visit specific validation
      if (ticket?.request_type === 'Client Factory Visit') {
        const errs = [];
        const vis = formData.cfv_visitors || [];
        if (vis.length === 0) errs.push('at least one Visitor');
        if (vis.some(v => !v.name?.trim() || !v.contact?.trim() || !v.email?.trim())) errs.push('Visitor Name, Contact and Email');
        if (vis.some(v => !v.company?.trim())) errs.push('Visitor Company');
        if (formData.cfv_primary_objective === 'Other' && !formData.cfv_primary_objective_other?.trim()) errs.push('primary purpose specification');
        if (formData.cfv_industry_segment === 'Other' && !formData.cfv_industry_segment_other?.trim()) errs.push('Industry / Segment specification');
        if (formData.cfv_food_required === 'Yes' && (formData.cfv_meals || []).length === 0) errs.push('at least one Meal');
        if (formData.cfv_stay_required === 'Yes' && !(parseInt(formData.cfv_stay_days) > 0)) errs.push('Number of Nights greater than 0');
        if ((formData.cfv_giveaway || []).includes('Others') && !formData.cfv_giveaway_other?.trim()) errs.push('Giveaway specification');
        if (!formData.cfv_visit_locations || formData.cfv_visit_locations.length === 0) errs.push('at least one Visit Location');
        if (errs.length > 0) {
          toast.error(`Please fill all mandatory fields: ${errs.join(', ')}`);
          return;
        }
      }
    } else {
      // Just saving draft - only basic validation
      if (!formData.description.trim()
        && !ticket?.request_type?.includes('Stationery')
        && !ticket?.request_type?.includes('Onboarding')
        && !ticket?.request_type?.includes('HR – Induction')
        && ticket?.request_type !== 'Client Factory Visit') {
        toast.error('Please provide a description');
        return;
      }
    }

    setSaving(true);
    try {
      // Upload induction photo if it's a new File
      let photoUrl = formData.induction_photo;
      if (photoUrl instanceof File) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoUrl);
        const photoRes = await fetch(`${API}/upload`, {
          method: 'POST',
          headers: getAuthHeader(),
          body: photoFormData
        });
        if (photoRes.ok) {
          const photoData = await photoRes.json();
          photoUrl = photoData.url;
        } else {
          photoUrl = ticket.induction_photo || null;
        }
      }

      const payload = {
        request_type: ticket.request_type,
        description: formData.description,
        priority: formData.priority,
        department: ticket.department,
        attachments: ticket.attachments || [],
        // All dynamic fields
        courier_type: formData.courier_type,
        courier_from: formData.courier_from,
        courier_to: formData.courier_to,
        courier_contents: formData.courier_contents,
        courier_pickup_point: formData.courier_pickup_point,
        guest_name: formData.guest_name,
        guest_company: formData.guest_company,
        guest_purpose: formData.guest_purpose,
        guest_date: formData.guest_date,
        guest_time: formData.guest_time,
        guest_arrival_date: formData.guest_arrival_date,
        guest_departure_date: formData.guest_departure_date,
        guest_food_required: formData.guest_food_required,
        guest_car_parking: formData.guest_car_parking,
        event_name: formData.event_name,
        event_date: formData.event_date,
        event_time: formData.event_time,
        event_location: formData.event_location,
        event_attendees: formData.event_attendees,
        event_requirements: formData.event_requirements,
        room_type: formData.room_type,
        room_location: formData.room_location,
        room_date: formData.room_date,
        room_start_time: formData.room_start_time,
        room_end_time: formData.room_end_time,
        room_attendees: formData.room_attendees,
        room_requirements: formData.room_requirements,
        promo_employee_name: formData.promo_employee_name,
        promo_designation: formData.promo_designation,
        promo_department: formData.promo_department,
        promo_effective_date: formData.promo_effective_date,
        promo_special_instructions: formData.promo_special_instructions,
        birthday_employee_name: formData.birthday_employee_name,
        birthday_date: formData.birthday_date,
        birthday_department: formData.birthday_department,
        birthday_location: formData.birthday_location,
        // HR Induction
        induction_employee_name: formData.induction_employee_name,
        induction_employee_id: formData.induction_employee_id,
        induction_designation: formData.induction_designation,
        induction_joining_date: formData.induction_joining_date,
        induction_location: formData.induction_location,
        induction_department: formData.induction_department,
        induction_employee_type: formData.induction_employee_type,
        induction_tshirt_size: formData.induction_tshirt_size,
        induction_need_id_card: formData.induction_need_id_card,
        induction_dob: formData.induction_dob,
        induction_date_of_issue: formData.induction_date_of_issue,
        induction_masked_aadhaar: formData.induction_masked_aadhaar,
        induction_blood_group: formData.induction_blood_group,
        induction_emergency_number: formData.induction_emergency_number,
        induction_office_address: formData.induction_office_address,
        induction_need_mediclaim: formData.induction_need_mediclaim,
        induction_mediclaim_members: formData.induction_mediclaim_members,
        induction_facility_visiting_card: formData.induction_facility_visiting_card,
        induction_facility_hotel: formData.induction_facility_hotel,
        induction_facility_transport: formData.induction_facility_transport,
        induction_food_required: formData.induction_food_required,
        induction_entity: formData.induction_entity,
        induction_photo: photoUrl,
        // Insurance
        insurance_employee_name: formData.insurance_employee_name,
        insurance_employee_id: formData.insurance_employee_id,
        insurance_exit_date: formData.insurance_exit_date,
        insurance_employees: formData.insurance_employees,
        // Stationery
        stationery_type: formData.stationery_type,
        stationery_items: formData.stationery_items,
        stationery_description: formData.stationery_description,
        stationery_required_by_date: formData.stationery_required_by_date,
        // Client Factory Visit
        cfv_brand: formData.cfv_brand,
        cfv_guest_name: formData.cfv_guest_name,
        cfv_company: formData.cfv_company,
        cfv_guests_count: formData.cfv_guests_count,
        cfv_purpose: formData.cfv_purpose,
        cfv_need_invite: formData.cfv_need_invite,
        cfv_photography: formData.cfv_photography,
        cfv_videography: formData.cfv_videography,
        cfv_transport: formData.cfv_transport,
        cfv_visitors: formData.cfv_visitors,
        cfv_brands: formData.cfv_brands,
        cfv_food_required: formData.cfv_food_required,
        cfv_meals: formData.cfv_meals,
        cfv_dietary: formData.cfv_dietary,
        cfv_stay_required: formData.cfv_stay_required,
        cfv_stay_days: formData.cfv_stay_days,
        cfv_giveaway: formData.cfv_giveaway,
        cfv_giveaway_other: formData.cfv_giveaway_other,
        cfv_car_parking: formData.cfv_car_parking,
        cfv_primary_objective: formData.cfv_primary_objective,
        cfv_primary_objective_other: formData.cfv_primary_objective_other,
        cfv_project_linked: formData.cfv_project_linked,
        cfv_project_name: formData.cfv_project_name,
        cfv_project_location: formData.cfv_project_location,
        cfv_project_stage: formData.cfv_project_stage,
        cfv_industry_segment: formData.cfv_industry_segment,
        cfv_industry_segment_other: formData.cfv_industry_segment_other,
        cfv_area_of_interest: formData.cfv_area_of_interest,
        cfv_areas_covered: formData.cfv_areas_covered,
        cfv_products_demonstrated: formData.cfv_products_demonstrated,
        cfv_lab_testing: formData.cfv_lab_testing,
        cfv_lab_testing_details: formData.cfv_lab_testing_details,
        cfv_team_members: formData.cfv_team_members,
        cfv_senior_mgmt: formData.cfv_senior_mgmt,
        cfv_presentation: formData.cfv_presentation,
        cfv_presentation_type: formData.cfv_presentation_type,
        cfv_conference_room: formData.cfv_conference_room,
        cfv_visit_locations: formData.cfv_visit_locations,
      };

      const res = await fetch(`${API}/tickets/${ticketId}/edit`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Changes saved');
        
        if (submitAfterSave && ticket.status === 'Draft') {
          const submitRes = await fetch(`${API}/tickets/${ticketId}/submit-draft`, {
            method: 'PUT',
            headers: getAuthHeader(),
            
          });
          
          if (submitRes.ok) {
            toast.success('Request submitted for approval');
          }
        }
        
        navigate(`/tickets/${ticketId}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save changes');
      }
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Render request-specific form fields
  const renderRequestTypeFields = () => {
    if (!ticket) return null;

    switch (ticket.request_type) {
      case 'Courier (Incoming / Outgoing)':
        return (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-lg">Courier Details</h3>
            <div className="space-y-2">
              <Label>Courier Type *</Label>
              <Select
                value={formData.courier_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, courier_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Incoming">Incoming</SelectItem>
                  <SelectItem value="Outgoing">Outgoing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Address *</Label>
                <Textarea
                  value={formData.courier_from}
                  onChange={(e) => setFormData(prev => ({ ...prev, courier_from: e.target.value }))}
                  placeholder="Sender address"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Pickup Point</Label>
                <Input
                  value={formData.courier_pickup_point}
                  onChange={(e) => setFormData(prev => ({ ...prev, courier_pickup_point: e.target.value }))}
                  placeholder="Pickup location"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>To Address *</Label>
              <Textarea
                value={formData.courier_to}
                onChange={(e) => setFormData(prev => ({ ...prev, courier_to: e.target.value }))}
                placeholder="Recipient address"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label>Contents Description *</Label>
              <Textarea
                value={formData.courier_contents}
                onChange={(e) => setFormData(prev => ({ ...prev, courier_contents: e.target.value }))}
                placeholder="What is being sent?"
                rows={2}
              />
            </div>
          </div>
        );

      case 'Guest Arrival':
        return (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-lg">Guest Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest Name *</Label>
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company/Organization *</Label>
                <Input
                  value={formData.guest_company}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_company: e.target.value }))}
                  placeholder="Company name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visit Date *</Label>
                <Input
                  type="date"
                  value={formData.guest_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Time *</Label>
                <TimePicker12h
                  value={formData.guest_time}
                  onChange={(val) => setFormData(prev => ({ ...prev, guest_time: val }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Purpose of Visit *</Label>
              <Textarea
                value={formData.guest_purpose}
                onChange={(e) => setFormData(prev => ({ ...prev, guest_purpose: e.target.value }))}
                placeholder="Reason for visit"
                rows={3}
              />
            </div>
          </div>
        );

      case 'Client Factory Visit': {
        const segs = formData.cfv_visit_locations || [];
        const CFV_MEALS = ['Breakfast', 'Lunch', 'Dinner'];
        const CFV_DIETARY = ['Veg', 'Non-Veg', 'Jain'];
        const CFV_GIVEAWAY = ['Univic Frames', 'Momentos', 'Royce', 'Bombay Sweet Shop / Equivalent Hampers', 'Others'];
        const CFV_SEGMENT = ['CRE', 'Data Centre', 'Control Rooms', 'Other'];
        const CFV_PURPOSE = ['Understand Production Capacity & Manufacturing Process', 'Product / Quality Inspection', 'Technical Discussion / Interaction with Technical Team', 'Product Demonstration & Product Range Understanding', 'Vendor / Supplier Evaluation', 'Other'];
        const CFV_PROJECT_STAGES = ['Concept', 'Design', 'Tendering', 'Execution', 'Completed'];
        const CFV_COUNTRIES = COUNTRIES;
        const foodOn = formData.cfv_food_required === 'Yes';
        const visitors = formData.cfv_visitors || [];
        const setVisitors = (v) => setFormData(prev => ({ ...prev, cfv_visitors: v, cfv_guests_count: String(v.length) }));
        const addVisitor = () => setVisitors([...visitors, { name: '', contact: '', email: '', designation: '', company: '', country: '', dietary: 'Veg' }]);
        const updVisitor = (i, k, val) => setVisitors(visitors.map((v, idx) => idx === i ? { ...v, [k]: val } : v));
        const rmVisitor = (i) => setVisitors(visitors.filter((_, idx) => idx !== i));
        const dietCounts = { Veg: 0, 'Non-Veg': 0, Jain: 0 };
        visitors.forEach(v => { if (v.dietary && dietCounts[v.dietary] !== undefined) dietCounts[v.dietary] += 1; });
        const toggleArr = (field, val) => setFormData(prev => { const a = prev[field] || []; return { ...prev, [field]: a.includes(val) ? a.filter(x => x !== val) : [...a, val] }; });
        const Chip = ({ field, val }) => {
          const on = (formData[field] || []).includes(val);
          return <button type="button" onClick={() => toggleArr(field, val)} data-testid={`${field}-${val}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-background hover:bg-muted'}`}>{val}</button>;
        };
        const YesNo = ({ label, field, testid }) => (
          <div className="space-y-2">
            <Label className="block text-sm leading-snug min-h-[2.25rem]">{label}</Label>
            <Select value={formData[field]} onValueChange={(v) => setFormData(prev => ({ ...prev, [field]: v }))}>
              <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
            </Select>
          </div>
        );
        return (
          <div className="space-y-5 border-t pt-4" data-testid="cfv-edit-form">
            <h3 className="font-semibold text-lg">Client Factory Visit</h3>

            {/* Visitors */}
            <div className="border rounded-xl p-4 space-y-3 bg-muted/10" data-testid="cfv-visitors">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h4 className="font-semibold">Visitors *</h4>
                  <p className="text-xs text-muted-foreground">Add every visitor with their company. No. of Guests: <span className="font-medium text-foreground" data-testid="cfv-guests-count">{visitors.length}</span></p>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addVisitor} data-testid="cfv-add-visitor"><Plus className="h-4 w-4 mr-1" />Add Visitor</Button>
              </div>
              {visitors.length === 0 && <p className="text-sm text-muted-foreground italic">No visitors added yet.</p>}
              {visitors.map((v, i) => (
                <div key={i} className="border rounded-lg p-3 bg-background space-y-2" data-testid={`cfv-visitor-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Visitor {i + 1}</span>
                    <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmVisitor(i)} data-testid={`cfv-visitor-remove-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    <Input placeholder="Name *" value={v.name} onChange={e => updVisitor(i, 'name', e.target.value)} data-testid={`cfv-visitor-name-${i}`} />
                    <Input placeholder="Contact *" value={v.contact} onChange={e => updVisitor(i, 'contact', e.target.value)} data-testid={`cfv-visitor-contact-${i}`} />
                    <Input placeholder="Email *" value={v.email} onChange={e => updVisitor(i, 'email', e.target.value)} data-testid={`cfv-visitor-email-${i}`} />
                    <Input placeholder="Company *" value={v.company || ''} onChange={e => updVisitor(i, 'company', e.target.value)} data-testid={`cfv-visitor-company-${i}`} />
                    <Input placeholder="Designation" value={v.designation} onChange={e => updVisitor(i, 'designation', e.target.value)} />
                    <Select value={v.country || ''} onValueChange={val => updVisitor(i, 'country', val)}>
                      <SelectTrigger data-testid={`cfv-visitor-country-${i}`}><SelectValue placeholder="Country" /></SelectTrigger>
                      <SelectContent>{CFV_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>

            {/* Visit Locations */}
            <div className="border rounded-xl p-4 space-y-3 bg-muted/10" data-testid="cfv-visit-locations">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h4 className="font-semibold">Visit Locations</h4>
                  <p className="text-xs text-muted-foreground">Add each location with its own date and timing.</p>
                </div>
                {!cfvSeg && <Button type="button" size="sm" variant="outline" onClick={startAddSeg} data-testid="cfv-add-location"><Plus className="h-4 w-4 mr-1" />Add Visit Location</Button>}
              </div>

              {segs.length > 0 && (
                <div className="overflow-x-auto rounded-lg border bg-background">
                  <table className="w-full text-sm border-collapse" data-testid="cfv-itinerary">
                    <thead>
                      <tr className="text-left text-xs text-muted-foreground border-b bg-muted/40">
                        <th className="py-2 px-3">Location</th><th className="py-2 px-3">Brand</th><th className="py-2 px-3">Entity</th>
                        <th className="py-2 px-3 whitespace-nowrap">Arrival</th><th className="py-2 px-3 whitespace-nowrap">Departure</th><th className="py-2 px-3">Duration</th><th className="py-2 px-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {segs.map((s, i) => (
                        <tr key={i} className="border-b last:border-0" data-testid={`cfv-seg-row-${i}`}>
                          <td className="py-2 px-3 font-medium">{s.location}</td>
                          <td className="py-2 px-3">{s.brand || '—'}</td>
                          <td className="py-2 px-3">{s.entity || '—'}</td>
                          <td className="py-2 px-3 whitespace-nowrap">{s.arrival_date} {s.expected_time || ''}</td>
                          <td className="py-2 px-3 whitespace-nowrap">{s.departure_date || '—'} {s.departure_time || ''}</td>
                          <td className="py-2 px-3">{s.duration}</td>
                          <td className="py-2 px-3 text-right whitespace-nowrap">
                            <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEditSeg(i)} data-testid={`cfv-edit-seg-${i}`}><Pencil className="h-4 w-4" /></Button>
                            <Button type="button" size="icon" variant="ghost" className="h-8 w-8" onClick={() => deleteSeg(i)} data-testid={`cfv-delete-seg-${i}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {cfvSeg && (
                <div className="border rounded-lg p-4 space-y-4 bg-background" data-testid="cfv-seg-editor">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Location *</Label>
                      <Select value={cfvSeg.location} onValueChange={(v) => setCfvSeg(s => ({ ...s, location: v }))}>
                        <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-location"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CFV_LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Brand to Visit</Label>
                      <Select value={cfvSeg.brand} onValueChange={(v) => setCfvSeg(s => ({ ...s, brand: v }))}>
                        <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-brand"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CFV_BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Entity</Label>
                      <Select value={cfvSeg.entity} onValueChange={(v) => setCfvSeg(s => ({ ...s, entity: v }))}>
                        <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-entity"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{CFV_ENTITIES.map(en => <SelectItem key={en} value={en}>{en}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Duration</Label>
                      <Select value={cfvSeg.duration} onValueChange={(v) => setCfvSeg(s => ({ ...s, duration: v }))}>
                        <SelectTrigger className="h-9 w-full" data-testid="cfv-seg-duration"><SelectValue /></SelectTrigger>
                        <SelectContent>{CFV_DURATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-1 border rounded-lg p-3 bg-muted/20">
                      <Label className="text-xs font-semibold">Arrival *</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <Input type="date" className="h-9 w-[150px]" value={cfvSeg.arrival_date} onChange={(e) => setCfvSeg(s => ({ ...s, arrival_date: e.target.value }))} data-testid="cfv-seg-date" />
                        <TimePicker12h value={cfvSeg.expected_time} onChange={(val) => setCfvSeg(s => ({ ...s, expected_time: val }))} data-testid="cfv-seg-time" />
                      </div>
                    </div>
                    <div className="space-y-1 border rounded-lg p-3 bg-muted/20">
                      <Label className="text-xs font-semibold">Departure *</Label>
                      <div className="flex flex-wrap items-center gap-2">
                        <Input type="date" className="h-9 w-[150px]" value={cfvSeg.departure_date} onChange={(e) => setCfvSeg(s => ({ ...s, departure_date: e.target.value }))} data-testid="cfv-seg-dep-date" />
                        <TimePicker12h value={cfvSeg.departure_time} onChange={(val) => setCfvSeg(s => ({ ...s, departure_time: val }))} data-testid="cfv-seg-dep-time" />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={cancelSeg}>Cancel</Button>
                    <Button type="button" size="sm" onClick={saveSeg} data-testid="cfv-save-seg">{cfvEditIndex !== null ? 'Update Location' : 'Add to Itinerary'}</Button>
                  </div>
                </div>
              )}

              {segs.length === 0 && !cfvSeg && <p className="text-sm text-muted-foreground italic">No visit locations added yet.</p>}
            </div>

            {/* Visit Details */}
            <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-visit-details">
              <h4 className="font-semibold">Visit Details</h4>
              <div className="space-y-2">
                <Label>What is the primary purpose of your visit?</Label>
                <Select value={formData.cfv_primary_objective} onValueChange={(v) => setFormData(prev => ({ ...prev, cfv_primary_objective: v }))}>
                  <SelectTrigger data-testid="cfv-primary-objective"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                  <SelectContent>{CFV_PURPOSE.map(p => <SelectItem key={p} value={p}>{p === 'Other' ? 'Other (please specify)' : p}</SelectItem>)}</SelectContent>
                </Select>
                {formData.cfv_primary_objective === 'Other' && (
                  <Input className="mt-2" placeholder="Please specify *" value={formData.cfv_primary_objective_other} onChange={(e) => setFormData(prev => ({ ...prev, cfv_primary_objective_other: e.target.value }))} data-testid="cfv-primary-objective-other" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <YesNo label="Is this visit linked to a specific project?" field="cfv_project_linked" testid="cfv-project-linked" />
                <div className="space-y-2">
                  <Label>Industry / Segment</Label>
                  <Select value={formData.cfv_industry_segment} onValueChange={(v) => setFormData(prev => ({ ...prev, cfv_industry_segment: v }))}>
                    <SelectTrigger data-testid="cfv-industry-segment"><SelectValue placeholder="Select segment" /></SelectTrigger>
                    <SelectContent>{CFV_SEGMENT.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {formData.cfv_industry_segment === 'Other' && (
                    <Input className="mt-2" placeholder="Please specify *" value={formData.cfv_industry_segment_other} onChange={(e) => setFormData(prev => ({ ...prev, cfv_industry_segment_other: e.target.value }))} data-testid="cfv-industry-segment-other" />
                  )}
                </div>
              </div>
              {formData.cfv_project_linked === 'Yes' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border rounded-lg p-3 bg-background" data-testid="cfv-project-details">
                  <div className="space-y-2">
                    <Label>Project Name</Label>
                    <Input value={formData.cfv_project_name} onChange={(e) => setFormData(prev => ({ ...prev, cfv_project_name: e.target.value }))} data-testid="cfv-project-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={formData.cfv_project_location} onChange={(e) => setFormData(prev => ({ ...prev, cfv_project_location: e.target.value }))} data-testid="cfv-project-location" />
                  </div>
                  <div className="space-y-2">
                    <Label>Project Stage</Label>
                    <Select value={formData.cfv_project_stage} onValueChange={(v) => setFormData(prev => ({ ...prev, cfv_project_stage: v }))}>
                      <SelectTrigger data-testid="cfv-project-stage"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent>{CFV_PROJECT_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            {/* Visit Requirements */}
            <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-requirements">
              <h4 className="font-semibold">Visit Requirements</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                <YesNo label="Need Invite" field="cfv_need_invite" testid="cfv-need-invite" />
                <YesNo label="Transport" field="cfv_transport" testid="cfv-transport" />
                <YesNo label="Photography" field="cfv_photography" testid="cfv-photography" />
                <YesNo label="Videography" field="cfv_videography" testid="cfv-videography" />
                <YesNo label="Car Parking Required" field="cfv_car_parking" testid="cfv-car-parking" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <YesNo label="Food Required" field="cfv_food_required" testid="cfv-food-required" />
              </div>
              {foodOn && (
                <div className="space-y-3 border rounded-lg p-3 bg-background">
                  <div className="space-y-2">
                    <Label>Meal Required</Label>
                    <div className="flex flex-wrap gap-2" data-testid="cfv-meals">{CFV_MEALS.map(m => <Chip key={m} field="cfv_meals" val={m} />)}</div>
                  </div>
                  <div className="space-y-2">
                    <Label>Dietary preference per visitor</Label>
                    {visitors.length === 0 && <p className="text-xs text-muted-foreground italic">Add visitors above to set their dietary preference.</p>}
                    <div className="space-y-2" data-testid="cfv-visitor-diet-list">
                      {visitors.map((v, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 bg-muted/20">
                          <span className="text-sm font-medium truncate">{v.name?.trim() || `Visitor ${i + 1}`}</span>
                          <Select value={v.dietary || ''} onValueChange={val => updVisitor(i, 'dietary', val)}>
                            <SelectTrigger className="h-9 w-40 shrink-0" data-testid={`cfv-visitor-dietary-${i}`}><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{CFV_DIETARY.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    {visitors.length > 0 && (
                      <div className="text-sm bg-indigo-50 border border-indigo-200 rounded-lg p-3" data-testid="cfv-dietary-summary">
                        <span className="font-medium">Total:</span>{' '}
                        <span data-testid="cfv-diet-veg">Veg: {dietCounts.Veg}</span> ·{' '}
                        <span data-testid="cfv-diet-nonveg">Non-Veg: {dietCounts['Non-Veg']}</span> ·{' '}
                        <span data-testid="cfv-diet-jain">Jain: {dietCounts.Jain}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <YesNo label="Stay Required" field="cfv_stay_required" testid="cfv-stay-required" />
                {formData.cfv_stay_required === 'Yes' && (
                  <div className="space-y-2">
                    <Label>Number of Nights</Label>
                    <Input type="number" min="1" value={formData.cfv_stay_days} onChange={(e) => setFormData(prev => ({ ...prev, cfv_stay_days: e.target.value }))} data-testid="cfv-stay-days" />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Giveaway / Momento</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-lg bg-background" data-testid="cfv-giveaway">{CFV_GIVEAWAY.map(g => <Chip key={g} field="cfv_giveaway" val={g} />)}</div>
                {(formData.cfv_giveaway || []).includes('Others') && (
                  <Input className="mt-2" placeholder="Please specify *" value={formData.cfv_giveaway_other} onChange={(e) => setFormData(prev => ({ ...prev, cfv_giveaway_other: e.target.value }))} data-testid="cfv-giveaway-other" />
                )}
              </div>
            </div>

            {/* Factory Visit Requirements */}
            <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-factory-requirements">
              <h4 className="font-semibold">Factory Visit Requirements</h4>
              <div className="space-y-2">
                <Label>Products / Solutions to be demonstrated</Label>
                <Textarea value={formData.cfv_products_demonstrated} onChange={(e) => setFormData(prev => ({ ...prev, cfv_products_demonstrated: e.target.value }))} rows={2} data-testid="cfv-products-demonstrated" />
              </div>
              <YesNo label="Lab Testing / Demonstration Required" field="cfv_lab_testing" testid="cfv-lab-testing" />
            </div>

            {/* Internal Participation */}
            <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-internal-participation">
              <h4 className="font-semibold">Internal Participation</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <YesNo label="Senior Management Presence Required" field="cfv_senior_mgmt" testid="cfv-senior-mgmt" />
              </div>
            </div>

            {/* Presentation & Meeting Requirements */}
            <div className="border rounded-xl p-4 space-y-4 bg-muted/10" data-testid="cfv-presentation-requirements">
              <h4 className="font-semibold">Presentation &amp; Meeting Requirements</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <YesNo label="Presentation Required" field="cfv_presentation" testid="cfv-presentation" />
                {formData.cfv_presentation === 'Yes' && (
                  <div className="space-y-2">
                    <Label>Type of Presentation</Label>
                    <Input value={formData.cfv_presentation_type} onChange={(e) => setFormData(prev => ({ ...prev, cfv_presentation_type: e.target.value }))} placeholder="e.g. Standard / Customised / Project-specific" data-testid="cfv-presentation-type" />
                  </div>
                )}
                <YesNo label="Conference Room Required" field="cfv_conference_room" testid="cfv-conference-room" />
              </div>
            </div>
          </div>
        );
      }

      case 'Organize an Event':
        return (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-lg">Event Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Event Name *</Label>
                <Input
                  value={formData.event_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_name: e.target.value }))}
                  placeholder="Event title"
                />
              </div>
              <div className="space-y-2">
                <Label>Location *</Label>
                <Input
                  value={formData.event_location}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_location: e.target.value }))}
                  placeholder="Venue"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.event_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Time *</Label>
                <TimePicker12h
                  value={formData.event_time}
                  onChange={(val) => setFormData(prev => ({ ...prev, event_time: val }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Attendees</Label>
                <Input
                  type="number"
                  value={formData.event_attendees}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_attendees: e.target.value }))}
                  placeholder="Number"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Requirements</Label>
              <Textarea
                value={formData.event_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, event_requirements: e.target.value }))}
                placeholder="Special requirements..."
                rows={3}
              />
            </div>
          </div>
        );

      case 'Meeting Room Booking':
        return (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-lg">Room Booking Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Room Type *</Label>
                <Select
                  value={formData.room_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, room_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Conference Room A">Conference Room A</SelectItem>
                    <SelectItem value="Conference Room B">Conference Room B</SelectItem>
                    <SelectItem value="Meeting Room 1">Meeting Room 1</SelectItem>
                    <SelectItem value="Meeting Room 2">Meeting Room 2</SelectItem>
                    <SelectItem value="Board Room">Board Room</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.room_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, room_date: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <TimePicker12h
                  value={formData.room_start_time}
                  onChange={(val) => setFormData(prev => ({ ...prev, room_start_time: val }))}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <TimePicker12h
                  value={formData.room_end_time}
                  onChange={(val) => setFormData(prev => ({ ...prev, room_end_time: val }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Attendees</Label>
                <Input
                  type="number"
                  value={formData.room_attendees}
                  onChange={(e) => setFormData(prev => ({ ...prev, room_attendees: e.target.value }))}
                  placeholder="Number"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Requirements</Label>
              <Textarea
                value={formData.room_requirements}
                onChange={(e) => setFormData(prev => ({ ...prev, room_requirements: e.target.value }))}
                placeholder="Projector, whiteboard, etc."
                rows={2}
              />
            </div>
          </div>
        );

      case 'Onboarding':
      case 'HR – Induction Kit Request':
        return (
          <div className="space-y-6 border-t pt-4">
            <h3 className="font-semibold text-lg">Onboarding Details</h3>
            
            {/* Employee Basic Details */}
            <div className="space-y-4">
              <h4 className="font-medium text-base border-b pb-2">Employee Basic Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Name *</Label>
                  <Input
                    value={formData.induction_employee_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_employee_name: e.target.value }))}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Employee ID *</Label>
                  <Input
                    value={formData.induction_employee_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_employee_id: e.target.value }))}
                    placeholder="Employee ID"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Designation *</Label>
                  <Input
                    value={formData.induction_designation}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_designation: e.target.value }))}
                    placeholder="Job title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Joining Date *</Label>
                  <Input
                    type="date"
                    value={formData.induction_joining_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_joining_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Location *</Label>
                  <Input
                    value={formData.induction_location}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_location: e.target.value }))}
                    placeholder="Office location"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Department *</Label>
                  <Input
                    value={formData.induction_department}
                    onChange={(e) => setFormData(prev => ({ ...prev, induction_department: e.target.value }))}
                    placeholder="Department name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Employee Type *</Label>
                  <Select
                    value={formData.induction_employee_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_employee_type: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Permanent">Permanent</SelectItem>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Intern">Intern</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Entity *</Label>
                  <Select
                    value={formData.induction_entity}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_entity: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select entity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UOSPL">UOSPL</SelectItem>
                      <SelectItem value="UAFPL">UAFPL</SelectItem>
                      <SelectItem value="UAPL">UAPL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>T-Shirt Size</Label>
                  <Select
                    value={formData.induction_tshirt_size}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_tshirt_size: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="XS">XS</SelectItem>
                      <SelectItem value="S">S</SelectItem>
                      <SelectItem value="M">M</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                      <SelectItem value="XL">XL</SelectItem>
                      <SelectItem value="XXL">XXL</SelectItem>
                      <SelectItem value="XXXL">XXXL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* ID Card Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h4 className="font-medium text-base">ID Card Required?</h4>
                <Select
                  value={formData.induction_need_id_card}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, induction_need_id_card: value }))}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.induction_need_id_card === 'yes' && (
                <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={formData.induction_dob}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_dob: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date of Issue</Label>
                      <Input
                        type="date"
                        value={formData.induction_date_of_issue}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_date_of_issue: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Blood Group</Label>
                      <Select
                        value={formData.induction_blood_group}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, induction_blood_group: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {BLOOD_GROUPS.map((bg) => (
                            <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Emergency Contact Number</Label>
                      <Input
                        value={formData.induction_emergency_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_emergency_number: e.target.value }))}
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Masked Aadhaar (Last 4 digits)</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-sm">XXXX-XXXX-</span>
                        <Input
                          value={formData.induction_masked_aadhaar}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setFormData(prev => ({ ...prev, induction_masked_aadhaar: value }));
                          }}
                          placeholder="1234"
                          maxLength={4}
                          className="w-24"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Office Address</Label>
                    <Textarea
                      value={formData.induction_office_address}
                      onChange={(e) => setFormData(prev => ({ ...prev, induction_office_address: e.target.value }))}
                      placeholder="Office address for ID card"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Mediclaim Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b pb-2">
                <h4 className="font-medium text-base">Mediclaim Required?</h4>
                <Select
                  value={formData.induction_need_mediclaim}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, induction_need_mediclaim: value }))}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.induction_need_mediclaim === 'yes' && (
                <div className="space-y-4 pl-4 border-l-2 border-primary/20">
                  {formData.induction_mediclaim_members?.map((member, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span className="font-medium">Family Member {index + 1}</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeMediclaimMember(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Full Name *</Label>
                          <Input
                            value={member.full_name}
                            onChange={(e) => updateMediclaimMember(index, 'full_name', e.target.value)}
                            placeholder="Full name"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Relation *</Label>
                          <Select
                            value={member.relation}
                            onValueChange={(value) => updateMediclaimMember(index, 'relation', value)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Select relation" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Self">Self</SelectItem>
                              <SelectItem value="Spouse">Spouse</SelectItem>
                              <SelectItem value="Son">Son</SelectItem>
                              <SelectItem value="Daughter">Daughter</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date of Birth *</Label>
                          <Input
                            type="date"
                            value={member.dob}
                            onChange={(e) => updateMediclaimMember(index, 'dob', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Date of Joining</Label>
                          <Input
                            type="date"
                            value={member.doj}
                            onChange={(e) => updateMediclaimMember(index, 'doj', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Grade</Label>
                          <Input
                            value={member.grade}
                            onChange={(e) => updateMediclaimMember(index, 'grade', e.target.value)}
                            placeholder="Grade/Level"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sum Insured</Label>
                          <Input
                            value={member.sum_insured}
                            onChange={(e) => updateMediclaimMember(index, 'sum_insured', e.target.value)}
                            placeholder="Amount"
                            className="h-9"
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addMediclaimMember}
                    className="w-full gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Family Member
                  </Button>
                </div>
              )}
            </div>

            {/* Facility Requirements */}
            <div className="space-y-4">
              <h4 className="font-medium text-base border-b pb-2">Facility Requirements</h4>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="visiting_card"
                    checked={formData.induction_facility_visiting_card}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, induction_facility_visiting_card: checked }))}
                  />
                  <Label htmlFor="visiting_card">Visiting Card</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hotel"
                    checked={formData.induction_facility_hotel}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, induction_facility_hotel: checked }))}
                  />
                  <Label htmlFor="hotel">Hotel</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="transport"
                    checked={formData.induction_facility_transport}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, induction_facility_transport: checked }))}
                  />
                  <Label htmlFor="transport">Transport</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Label>Food Required:</Label>
                  <Select
                    value={formData.induction_food_required}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, induction_food_required: value }))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        );

      case 'Insurance Removal Request':
        return (
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-lg">Insurance Removal Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee Name *</Label>
                <Input
                  value={formData.insurance_employee_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, insurance_employee_name: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div className="space-y-2">
                <Label>Employee ID *</Label>
                <Input
                  value={formData.insurance_employee_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, insurance_employee_id: e.target.value }))}
                  placeholder="Employee ID"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Exit Date *</Label>
              <Input
                type="date"
                value={formData.insurance_exit_date}
                onChange={(e) => setFormData(prev => ({ ...prev, insurance_exit_date: e.target.value }))}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </Layout>
    );
  }

  if (!canEdit) {
    return (
      <Layout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-muted-foreground">
                This ticket cannot be edited. Only Draft or Open tickets can be modified by the creator.
              </p>
              <div className="flex justify-center mt-4">
                <Button onClick={() => navigate(`/tickets/${ticketId}`)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Ticket
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/tickets/${ticketId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Edit Request</h1>
            <p className="text-muted-foreground">
              {ticket.ticket_id} - {ticket.request_type}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Request Details</CardTitle>
            <CardDescription>
              Update the details of your {ticket.status.toLowerCase()} request
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Request Type</Label>
                <Input value={ticket.request_type} disabled className="bg-muted" />
              </div>
            </div>

            {/* Request-specific fields */}
            {renderRequestTypeFields()}

            {/* Description - for non-specialized request types */}
            {!['Stationery Request'].includes(ticket.request_type) && (
              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your request..."
                  rows={4}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => navigate(`/tickets/${ticketId}`)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleSave(false)}
                disabled={saving}
                className="gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </Button>
              {ticket.status === 'Draft' && (
                <Button
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  className="gap-2"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Save & Submit
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
